import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Inventory as InventoryIcon,
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  UnfoldMore as UnfoldIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Inbox as InboxIcon,
  Category as CategoryIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import StatsCard from "../common/StatsCard";
import StatusChip from "../common/StatusChip";
import EmptyState from "../common/EmptyState";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const ITEMS_PER_PAGE = 5;

const InventoryList = () => {
  const navigate = useNavigate();
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterField, setFilterField] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState("table"); // "table" or "cards"
  const [sortField, setSortField] = useState("itemID");
  const [sortDirection, setSortDirection] = useState("asc");
 
  const fetchInventoryItems = async (id = "", idType = "itemID") => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      let url = `${BASE_URL}/api/inventory`;

      if (id) {
        if (idType === "itemID") {
          url = `${BASE_URL}/api/inventory/${id}`;
        } else if (idType === "employeeID") {
          url = `${BASE_URL}/api/inventory/employee/${id}`;
        }
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Error fetching inventory items");
      }

      const data = await response.json();
      if (id && idType === "itemID") {
        setInventoryItems([data]);
      } else {
        setInventoryItems(data);
      }
    } catch (err) {
      console.error("Error fetching inventory items:", err);
      setError("Error fetching inventory items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryItems();
  }, []);


  // Sorting function
  const sortItems = (items) => {
    return [...items].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case "itemID":
          aValue = a.itemID;
          bValue = b.itemID;
          break;
        case "itemName":
          aValue = a.itemName;
          bValue = b.itemName;
          break;
        case "category":
          aValue = a.category;
          bValue = b.category;
          break;
        case "price":
          aValue = parseFloat(a.price);
          bValue = parseFloat(b.price);
          break;
        case "purchaseDate":
          aValue = new Date(a.purchaseDate);
          bValue = new Date(b.purchaseDate);
          break;
        default:
          aValue = a[sortField] || "";
          bValue = b[sortField] || "";
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const filteredItems = inventoryItems.filter((item) => {
    const term = searchTerm.toLowerCase().trim();
    if (filterField === "all") {
      if (!term) return true;
      const latest =
        item.assignmentHistory && item.assignmentHistory.length
          ? item.assignmentHistory[item.assignmentHistory.length - 1]
          : {};
      return (
        item.itemID.toLowerCase().includes(term) ||
        item.boxID?.toLowerCase().includes(term) ||
        item.serialNumber?.toLowerCase().includes(term) ||
        item.itemName.toLowerCase().includes(term) ||
        item.category.toLowerCase().includes(term) ||
        item.brandModel.toLowerCase().includes(term) ||
        item.supplier.toLowerCase().includes(term) ||
        new Date(item.purchaseDate)
          .toLocaleDateString()
          .toLowerCase()
          .includes(term) ||
        new Date(item.warrantyExpiryDate)
          .toLocaleDateString()
          .toLowerCase()
          .includes(term) ||
        item.price.toString().toLowerCase().includes(term) ||
        (latest.assignedTo && latest.assignedTo.toLowerCase().includes(term)) ||
        (latest.assignedBy && latest.assignedBy.toLowerCase().includes(term)) ||
        (latest.assignmentDate &&
          new Date(latest.assignmentDate)
            .toLocaleDateString()
            .toLowerCase()
            .includes(term)) ||
        (latest.returnDate &&
          new Date(latest.returnDate)
            .toLocaleDateString()
            .toLowerCase()
            .includes(term)) ||
        (latest.condition && latest.condition.toLowerCase().includes(term))
      );
    } else {
      if (!term) return true;
      switch (filterField) {
        case "itemID":
          return item.itemID.toLowerCase().includes(term);
        case "boxID":
          return item.boxID?.toLowerCase().includes(term);
        case "serialNumber":
          return item.serialNumber?.toLowerCase().includes(term);
        case "itemName":
          return item.itemName.toLowerCase().includes(term);
        case "category":
          return item.category.toLowerCase().includes(term);
        case "brandModel":
          return item.brandModel.toLowerCase().includes(term);
        case "supplier":
          return item.supplier.toLowerCase().includes(term);
        case "purchaseDate":
          return new Date(item.purchaseDate)
            .toLocaleDateString()
            .toLowerCase()
            .includes(term);
        case "warrantyExpiryDate":
          return new Date(item.warrantyExpiryDate)
            .toLocaleDateString()
            .toLowerCase()
            .includes(term);
        case "price":
          return item.price.toString().toLowerCase().includes(term);
        case "assignedTo": {
          const latest =
            item.assignmentHistory && item.assignmentHistory.length
              ? item.assignmentHistory[item.assignmentHistory.length - 1]
              : {};
          return (
            latest.assignedTo && latest.assignedTo.toLowerCase().includes(term)
          );
        }
        case "assignedBy": {
          const latest =
            item.assignmentHistory && item.assignmentHistory.length
              ? item.assignmentHistory[item.assignmentHistory.length - 1]
              : {};
          return (
            latest.assignedBy && latest.assignedBy.toLowerCase().includes(term)
          );
        }
        case "assignmentDate": {
          const latest =
            item.assignmentHistory && item.assignmentHistory.length
              ? item.assignmentHistory[item.assignmentHistory.length - 1]
              : {};
          return (
            latest.assignmentDate &&
            new Date(latest.assignmentDate)
              .toLocaleDateString()
              .toLowerCase()
              .includes(term)
          );
        }
        case "returnDate": {
          const latest =
            item.assignmentHistory && item.assignmentHistory.length
              ? item.assignmentHistory[item.assignmentHistory.length - 1]
              : {};
          return (
            latest.returnDate &&
            new Date(latest.returnDate)
              .toLocaleDateString()
              .toLowerCase()
              .includes(term)
          );
        }
        case "condition": {
          const latest =
            item.assignmentHistory && item.assignmentHistory.length
              ? item.assignmentHistory[item.assignmentHistory.length - 1]
              : {};
          return (
            latest.condition && latest.condition.toLowerCase().includes(term)
          );
        }
        default:
          return true;
      }
    }
  });

  const sortedItems = sortItems(filteredItems);
  const totalPages = Math.ceil(sortedItems.length / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedItems = sortedItems.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filterField, searchTerm]);

  const handleDelete = async (itemID) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this item?"
    );
    if (confirmed) {
      try {
        const token = sessionStorage.getItem("token");
        await fetch(`${BASE_URL}/api/inventory/${itemID}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        setInventoryItems(
          inventoryItems.filter((item) => item.itemID !== itemID)
        );
        setSuccess("Item deleted successfully!");
      } catch (err) {
        console.error("Error deleting inventory item:", err);
        setError("Error deleting inventory item");
      }
    }
  };

  const handleEdit = (item) => {
    const formatDate = (date) => {
      if (!date) return "";
      const d = new Date(date);
      return d.toISOString().split("T")[0];
    };

    setEditItem({
      ...item,
      purchaseDate: formatDate(item.purchaseDate),
      warrantyExpiryDate: formatDate(item.warrantyExpiryDate),
      assignmentDate: formatDate(item.assignmentDate),
      returnDate: formatDate(item.returnDate),
    });
    setModalOpen(true);
  };

  const handleSaveChanges = async () => {
    setLoading(true);
    setError(null);
    setSuccess("");
    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}/api/inventory/${editItem.itemID}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editItem),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Error updating inventory item");
      }

      const updatedItem = await response.json();
      setInventoryItems(
        inventoryItems.map((item) =>
          item.itemID === updatedItem.itemID ? updatedItem : item
        )
      );
      setModalOpen(false);
      setSuccess("Inventory item updated successfully!");
    } catch (err) {
      console.error("Error updating inventory item:", err);
      setError("Error updating inventory item");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "price") {
      const numericValue = Number(value);
      if (
        value !== "" &&
        (isNaN(numericValue) ||
          numericValue <= 0 ||
          !Number.isInteger(numericValue))
      ) {
        return;
      }
    }
    setEditItem({ ...editItem, [name]: value });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort icon rendering moved to renderSortIcon in the return block

  const assignedCount = inventoryItems.filter(item => {
    const latest = item.assignmentHistory?.length
      ? item.assignmentHistory[item.assignmentHistory.length - 1]
      : {};
    return latest.assignedTo && !latest.returnDate;
  }).length;

  const availableCount = inventoryItems.filter(item => {
    const latest = item.assignmentHistory?.length
      ? item.assignmentHistory[item.assignmentHistory.length - 1]
      : {};
    return !latest.assignedTo || latest.returnDate;
  }).length;

  const renderSortIcon = (field) => {
    if (sortField !== field) return <UnfoldIcon sx={{ fontSize: 16, opacity: 0.4 }} />;
    return sortDirection === "asc"
      ? <ArrowUpIcon sx={{ fontSize: 16 }} />
      : <ArrowDownIcon sx={{ fontSize: 16 }} />;
  };

  if (loading) {
    return (
      <PageContainer>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 12 }}>
          <CircularProgress size={40} sx={{ color: "#667eea", mb: 2 }} />
          <Typography sx={{ color: "#8b8fa3", fontSize: "0.875rem" }}>Loading inventory items...</Typography>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Inventory Management"
        subtitle="Comprehensive asset tracking and management dashboard"
        icon={<InventoryIcon />}
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={() => navigate("/AssignInventoryToEmployee")}
              sx={{
                borderColor: "#eef0f6",
                color: "#1a1a2e",
                fontWeight: 600,
                fontSize: "0.875rem",
                textTransform: "none",
                borderRadius: "8px",
                px: 2,
                "&:hover": { borderColor: "#667eea", color: "#667eea", bgcolor: "#eef0ff" },
              }}
            >
              Assign Item
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/InventoryForm")}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                fontWeight: 600,
                fontSize: "0.875rem",
                textTransform: "none",
                borderRadius: "8px",
                px: 2,
                boxShadow: "none",
                "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)", boxShadow: "none" },
              }}
            >
              Add New Item
            </Button>
          </>
        }
      />

      {/* Success/Error Snackbar */}
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess("")} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setSuccess("")} severity="success" variant="filled" sx={{ borderRadius: "8px" }}>{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError(null)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setError(null)} severity="error" variant="filled" sx={{ borderRadius: "8px" }}>{error}</Alert>
      </Snackbar>

      {/* Stats Row */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatsCard label="Total Items" value={inventoryItems.length} icon={<InventoryIcon />} color="#667eea" bgColor="#EBF0FF" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatsCard label="Assigned" value={assignedCount} icon={<PersonIcon />} color="#0369A1" bgColor="#E0F2FE" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatsCard label="Available" value={availableCount} icon={<CategoryIcon />} color="#065F46" bgColor="#D1FAE5" />
        </Grid>
      </Grid>

      {/* Search/Filter/Toggle Bar */}
      <Paper elevation={0} sx={{ border: "1px solid #eef0f6", borderRadius: "16px", p: 2.5, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: { xs: "stretch", md: "center" }, flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search inventory items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#8b8fa3", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                bgcolor: "#f5f7fb",
                fontSize: "0.875rem",
                "& fieldset": { borderColor: "#eef0f6" },
                "&:hover fieldset": { borderColor: "#CBD5E1" },
                "&.Mui-focused fieldset": { borderColor: "#667eea" },
              },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={filterField}
              onChange={(e) => setFilterField(e.target.value)}
              displayEmpty
              startAdornment={
                <InputAdornment position="start">
                  <FilterIcon sx={{ color: "#8b8fa3", fontSize: 20 }} />
                </InputAdornment>
              }
              sx={{
                borderRadius: "8px",
                bgcolor: "#f5f7fb",
                fontSize: "0.875rem",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#eef0f6" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#667eea" },
              }}
            >
              <MenuItem value="all">All Fields</MenuItem>
              <MenuItem value="itemID">Item ID</MenuItem>
              <MenuItem value="boxID">Box ID</MenuItem>
              <MenuItem value="serialNumber">Serial Number</MenuItem>
              <MenuItem value="itemName">Item Name</MenuItem>
              <MenuItem value="category">Category</MenuItem>
              <MenuItem value="brandModel">Brand/Model</MenuItem>
              <MenuItem value="supplier">Supplier</MenuItem>
              <MenuItem value="price">Price</MenuItem>
              <MenuItem value="assignedTo">Assigned To</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, val) => val && setViewMode(val)}
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  border: "1px solid #eef0f6",
                  borderRadius: "8px !important",
                  px: 1.5,
                  py: 0.75,
                  color: "#8b8fa3",
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  "&.Mui-selected": { bgcolor: "#eef0ff", color: "#667eea", borderColor: "#667eea" },
                },
              }}
            >
              <ToggleButton value="table"><ViewListIcon sx={{ fontSize: 18, mr: 0.5 }} /> Table</ToggleButton>
              <ToggleButton value="cards"><ViewModuleIcon sx={{ fontSize: 18, mr: 0.5 }} /> Cards</ToggleButton>
            </ToggleButtonGroup>
            <Typography sx={{ color: "#8b8fa3", fontSize: "0.8125rem", whiteSpace: "nowrap" }}>
              {sortedItems.length} of {inventoryItems.length} items
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Table View */}
      {viewMode === "table" ? (
        <Paper elevation={0} sx={{ border: "1px solid #eef0f6", borderRadius: "16px", overflow: "hidden" }}>
          <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  {[
                    { field: "itemID", label: "ITEM ID", sortable: true },
                    { field: "boxID", label: "BOX ID", sortable: true },
                    { field: null, label: "SERIAL NO." },
                    { field: null, label: "STOCK" },
                    { field: "itemName", label: "ITEM NAME", sortable: true },
                    { field: "category", label: "CATEGORY", sortable: true },
                    { field: null, label: "BRAND/MODEL" },
                    { field: "price", label: "PRICE", sortable: true },
                    { field: null, label: "STATUS" },
                    { field: null, label: "ASSIGNED TO" },
                    { field: null, label: "NOTES" },
                    { field: null, label: "ACTIONS" },
                  ].map((col, idx) => (
                    <TableCell
                      key={idx}
                      onClick={col.sortable ? () => handleSort(col.field) : undefined}
                      sx={{
                        bgcolor: "#f5f7fb",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: "#5a5f7a",
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        borderBottom: "2px solid #f0f0f5",
                        cursor: col.sortable ? "pointer" : "default",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                        py: 1.5,
                        "&:hover": col.sortable ? { color: "#667eea" } : {},
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {col.label}
                        {col.sortable && renderSortIcon(col.field)}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedItems.length > 0 ? (
                  paginatedItems.map((item) => {
                    const latestAssignment = item.assignmentHistory?.length
                      ? item.assignmentHistory[item.assignmentHistory.length - 1]
                      : {};
                    const status = latestAssignment.returnDate
                      ? "Available"
                      : latestAssignment.assignedTo
                      ? "Assigned"
                      : "Available";

                    return (
                      <TableRow key={item.itemID} sx={{ "&:hover": { bgcolor: "#f5f7fb" }, "& td": { borderBottom: "1px solid #f0f2f8" } }}>
                        <TableCell sx={{ fontSize: "0.875rem", color: "#667eea", fontWeight: 600 }}>{item.itemID}</TableCell>
                        <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>{item.boxID || "-"}</TableCell>
                        <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>{item.serialNumber || "-"}</TableCell>
                        <TableCell>
                          <Chip label={item.stock} size="small" sx={{ bgcolor: "#f5f7fb", color: "#5a5f7a", fontWeight: 600, fontSize: "0.75rem", height: 24, minWidth: 32 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", fontWeight: 600 }}>{item.itemName}</TableCell>
                        <TableCell>
                          <Chip label={item.category} size="small" variant="outlined" sx={{ fontSize: "0.75rem", borderColor: "#eef0f6", color: "#5a5f7a", height: 24 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>{item.brandModel}</TableCell>
                        <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", fontWeight: 600 }}>${item.price}</TableCell>
                        <TableCell>
                          <StatusChip status={status} />
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>{latestAssignment.assignedTo || "-"}</TableCell>
                        <TableCell sx={{ fontSize: "0.875rem", color: "#8b8fa3", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {item.notes ? (
                            <Tooltip title={item.notes} arrow>
                              <span>{item.notes.length > 20 ? `${item.notes.substring(0, 20)}...` : item.notes}</span>
                            </Tooltip>
                          ) : "-"}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEdit(item)} sx={{ color: "#8b8fa3", "&:hover": { color: "#667eea", bgcolor: "#eef0ff" } }}>
                                <EditIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleDelete(item.itemID)} sx={{ color: "#8b8fa3", "&:hover": { color: "#DC2626", bgcolor: "#FEE2E2" } }}>
                                <DeleteIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={12} sx={{ border: 0 }}>
                      <EmptyState
                        icon={<InboxIcon sx={{ fontSize: 28 }} />}
                        title="No inventory items found"
                        subtitle="Try adjusting your search or filter criteria"
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination inside the Paper */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5, borderTop: "1px solid #eef0f6" }}>
            <Typography sx={{ color: "#8b8fa3", fontSize: "0.8125rem" }}>
              Showing {startIndex + 1} to {Math.min(startIndex + ITEMS_PER_PAGE, sortedItems.length)} of {sortedItems.length} items
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton
                size="small"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                sx={{ color: "#8b8fa3", "&:disabled": { opacity: 0.4 } }}
              >
                <ChevronLeftIcon sx={{ fontSize: 20 }} />
              </IconButton>
              {Array.from({ length: totalPages }, (_, index) => {
                const pageNum = index + 1;
                const isVisible = pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);
                if (!isVisible && pageNum === currentPage - 2) {
                  return <Typography key={pageNum} sx={{ color: "#8b8fa3", px: 0.5, fontSize: "0.8125rem" }}>...</Typography>;
                }
                if (!isVisible && pageNum === currentPage + 2) {
                  return <Typography key={pageNum} sx={{ color: "#8b8fa3", px: 0.5, fontSize: "0.8125rem" }}>...</Typography>;
                }
                if (!isVisible) return null;
                return (
                  <Button
                    key={pageNum}
                    size="small"
                    onClick={() => setCurrentPage(pageNum)}
                    sx={{
                      minWidth: 32,
                      height: 32,
                      borderRadius: "6px",
                      fontSize: "0.8125rem",
                      fontWeight: currentPage === pageNum ? 700 : 500,
                      color: currentPage === pageNum ? "#fff" : "#8b8fa3",
                      bgcolor: currentPage === pageNum ? "#667eea" : "transparent",
                      "&:hover": { bgcolor: currentPage === pageNum ? "#5a6fd8" : "#f0f2f8" },
                    }}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <IconButton
                size="small"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
                sx={{ color: "#8b8fa3", "&:disabled": { opacity: 0.4 } }}
              >
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      ) : (
        /* Card View */
        <>
          {paginatedItems.length > 0 ? (
            <Grid container spacing={2.5}>
              {paginatedItems.map((item) => {
                const latestAssignment = item.assignmentHistory?.length
                  ? item.assignmentHistory[item.assignmentHistory.length - 1]
                  : {};
                const status = latestAssignment.returnDate
                  ? "Available"
                  : latestAssignment.assignedTo
                  ? "Assigned"
                  : "Available";

                return (
                  <Grid item xs={12} sm={6} lg={4} key={item.itemID}>
                    <Paper elevation={0} sx={{ border: "1px solid #eef0f6", borderRadius: "16px", p: 3, transition: "all 0.2s", "&:hover": { borderColor: "#CBD5E1", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" } }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "#1a1a2e", mb: 0.25 }}>{item.itemName}</Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: "#667eea", fontWeight: 600 }}>{item.itemID}</Typography>
                        </Box>
                        <StatusChip status={status} />
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2.5 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Category</Typography>
                          <Typography sx={{ fontSize: "0.8125rem", color: "#1a1a2e", fontWeight: 500 }}>{item.category}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Brand/Model</Typography>
                          <Typography sx={{ fontSize: "0.8125rem", color: "#1a1a2e", fontWeight: 500 }}>{item.brandModel}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Price</Typography>
                          <Typography sx={{ fontSize: "0.8125rem", color: "#1a1a2e", fontWeight: 600 }}>${item.price}</Typography>
                        </Box>
                        {item.boxID && (
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Box ID</Typography>
                            <Typography sx={{ fontSize: "0.8125rem", color: "#1a1a2e", fontWeight: 500 }}>{item.boxID}</Typography>
                          </Box>
                        )}
                        {latestAssignment.assignedTo && (
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Assigned To</Typography>
                            <Typography sx={{ fontSize: "0.8125rem", color: "#1a1a2e", fontWeight: 500 }}>{latestAssignment.assignedTo}</Typography>
                          </Box>
                        )}
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Stock</Typography>
                          <Chip label={item.stock} size="small" sx={{ bgcolor: "#f5f7fb", color: "#5a5f7a", fontWeight: 600, fontSize: "0.75rem", height: 22 }} />
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1, pt: 2, borderTop: "1px solid #f0f2f8" }}>
                        <Button
                          size="small"
                          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                          onClick={() => handleEdit(item)}
                          sx={{ flex: 1, fontSize: "0.8125rem", textTransform: "none", color: "#667eea", borderRadius: "8px", "&:hover": { bgcolor: "#eef0ff" } }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                          onClick={() => handleDelete(item.itemID)}
                          sx={{ flex: 1, fontSize: "0.8125rem", textTransform: "none", color: "#DC2626", borderRadius: "8px", "&:hover": { bgcolor: "#FEE2E2" } }}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Paper elevation={0} sx={{ border: "1px solid #eef0f6", borderRadius: "16px" }}>
              <EmptyState
                icon={<InboxIcon sx={{ fontSize: 28 }} />}
                title="No inventory items found"
                subtitle="Try adjusting your search or filter criteria"
              />
            </Paper>
          )}

          {/* Pagination for card view */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 3 }}>
            <Typography sx={{ color: "#8b8fa3", fontSize: "0.8125rem" }}>
              Showing {startIndex + 1} to {Math.min(startIndex + ITEMS_PER_PAGE, sortedItems.length)} of {sortedItems.length} items
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton size="small" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} sx={{ color: "#8b8fa3", "&:disabled": { opacity: 0.4 } }}>
                <ChevronLeftIcon sx={{ fontSize: 20 }} />
              </IconButton>
              {Array.from({ length: totalPages }, (_, index) => {
                const pageNum = index + 1;
                const isVisible = pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);
                if (!isVisible && pageNum === currentPage - 2) return <Typography key={pageNum} sx={{ color: "#8b8fa3", px: 0.5, fontSize: "0.8125rem" }}>...</Typography>;
                if (!isVisible && pageNum === currentPage + 2) return <Typography key={pageNum} sx={{ color: "#8b8fa3", px: 0.5, fontSize: "0.8125rem" }}>...</Typography>;
                if (!isVisible) return null;
                return (
                  <Button
                    key={pageNum}
                    size="small"
                    onClick={() => setCurrentPage(pageNum)}
                    sx={{
                      minWidth: 32, height: 32, borderRadius: "6px", fontSize: "0.8125rem",
                      fontWeight: currentPage === pageNum ? 700 : 500,
                      color: currentPage === pageNum ? "#fff" : "#8b8fa3",
                      bgcolor: currentPage === pageNum ? "#667eea" : "transparent",
                      "&:hover": { bgcolor: currentPage === pageNum ? "#5a6fd8" : "#f0f2f8" },
                    }}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <IconButton size="small" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} sx={{ color: "#8b8fa3", "&:disabled": { opacity: 0.4 } }}>
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>
        </>
      )}

      {/* Edit Modal */}
      <Dialog
        open={modalOpen && !!editItem}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "#f5f7fb", borderBottom: "2px solid #f0f0f5", py: 2, px: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "8px", bgcolor: "#eef0ff", display: "flex", alignItems: "center", justifyContent: "center", color: "#667eea" }}>
              <EditIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1.125rem", color: "#1a1a2e" }}>Edit Inventory Item</Typography>
              <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Update item details and information</Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setModalOpen(false)} size="small" sx={{ color: "#8b8fa3" }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        {editItem && (
          <DialogContent sx={{ p: 3 }}>
            {/* Basic Information */}
            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e", mb: 2, mt: 1 }}>Basic Information</Typography>
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Item Name" name="itemName" value={editItem.itemName || ''} onChange={handleInputChange} required
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem", "& fieldset": { borderColor: "#eef0f6" } } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Category" name="category" value={editItem.category || ''} onChange={handleInputChange} required
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem", "& fieldset": { borderColor: "#eef0f6" } } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Brand/Model" name="brandModel" value={editItem.brandModel || ''} onChange={handleInputChange} required
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem", "& fieldset": { borderColor: "#eef0f6" } } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Supplier" name="supplier" value={editItem.supplier || ''} onChange={handleInputChange} required
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem", "& fieldset": { borderColor: "#eef0f6" } } }} />
              </Grid>
            </Grid>

            {/* Financial & Date */}
            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e", mb: 2 }}>Financial & Date Information</Typography>
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Price (USD)" name="price" type="number" value={editItem.price || ''} onChange={handleInputChange} required
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem", "& fieldset": { borderColor: "#eef0f6" } } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Condition</InputLabel>
                  <Select name="condition" value={editItem.condition || ''} onChange={handleInputChange} label="Condition" required
                    sx={{ borderRadius: "8px", fontSize: "0.875rem", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#eef0f6" } }}>
                    <MenuItem value="">Select condition</MenuItem>
                    <MenuItem value="New">New</MenuItem>
                    <MenuItem value="Excellent">Excellent</MenuItem>
                    <MenuItem value="Good">Good</MenuItem>
                    <MenuItem value="Fair">Fair</MenuItem>
                    <MenuItem value="Poor">Poor</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Purchase Date" name="purchaseDate" type="date" value={editItem.purchaseDate || ''} onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }} required
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem", "& fieldset": { borderColor: "#eef0f6" } } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Warranty Expiry Date" name="warrantyExpiryDate" type="date" value={editItem.warrantyExpiryDate || ''} onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }} required
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem", "& fieldset": { borderColor: "#eef0f6" } } }} />
              </Grid>
            </Grid>

            {/* Additional */}
            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e", mb: 2 }}>Additional Information</Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Box ID" name="boxID" value={editItem.boxID || ''} onChange={handleInputChange}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem", "& fieldset": { borderColor: "#eef0f6" } } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Serial Number" name="serialNumber" value={editItem.serialNumber || ''} onChange={handleInputChange}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem", "& fieldset": { borderColor: "#eef0f6" } } }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Notes" name="notes" value={editItem.notes || ''} onChange={handleInputChange} multiline rows={3}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem", "& fieldset": { borderColor: "#eef0f6" } } }} />
              </Grid>
            </Grid>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #eef0f6", bgcolor: "#f5f7fb", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "0.75rem", color: "#8b8fa3" }}>* Required fields</Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              onClick={() => setModalOpen(false)}
              variant="outlined"
              sx={{ borderColor: "#eef0f6", color: "#8b8fa3", textTransform: "none", borderRadius: "8px", fontWeight: 600, fontSize: "0.875rem", "&:hover": { borderColor: "#CBD5E1", bgcolor: "#f5f7fb" } }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveChanges}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon sx={{ fontSize: 18 }} />}
              sx={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", textTransform: "none", borderRadius: "8px", fontWeight: 600, fontSize: "0.875rem", boxShadow: "none", "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)", boxShadow: "none" } }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default InventoryList;
